// ── propSelection — seed the right-side panel from a Player Props ROW ──
//
// #149: clicking a prop row/card previously only toggled the inline expansion,
// so the right-side panel silently kept whatever it already held — on first
// load, the parent game's moneyline. Opening a prop row is a market selection,
// so it must seed the ticket with the EXACT threshold market, the way the
// price pills and rung strip already do.
//
// The rules encoded here, shared by the compact and detailed wiring:
//   • re-clicking the OPEN row collapses only → null. The panel remounts on
//     game.key, so any reseed would wipe in-progress order entry;
//   • venue: see `resolveRungVenue` below. Exported, and `handleSelectRung`
//     uses it too — one rule for every prop selection path, not two;
//   • a bookless rung fails CLOSED to null — never to the parent moneyline.
//
// Pure — no store, hooks or React.

import type { TradeTicketContext, VenueBook } from '../ValueDashboard/types';
import { chipKeyFor, type LadderTarget } from './ladderRows';

export type PropVenue = 'kalshi' | 'polymarket';

/** Which venue owns the open ticket, and for which ladder (openBandTicket). */
export interface PropTicketOwner {
  key: string;
  venue: PropVenue;
}

/**
 * Which venue a rung opens on. Shared by BOTH prop paths — row open here,
 * threshold strip in `handleSelectRung`.
 *
 * - Sticky venue only if the same ladder owns the ticket AND that venue lists
 *   this rung. Else Kalshi-first (most props are Kalshi-only).
 * - The `listed` guard is the point: Poly-owned ladder → Kalshi-only rung left
 *   a dead Poly form. Converse for Poly-only.
 * - Books resolve on the rung's OWN side. A synth-level `kalshi != null` passes
 *   for `{away: null, home: book}` and picks a venue missing that side.
 * - Bookless rung → null. Fails closed. (Strip can't hit it —
 *   `buildLadderTableRows` drops bookless rungs before `rungTargets`.)
 */
export function resolveRungVenue(args: {
  target: LadderTarget;
  /** The key ticket ownership is stored by — the ladder groupKey. */
  ticketKey: string;
  openTicket: PropTicketOwner | null;
}): { venue: PropVenue; kalshi: VenueBook | null; polymarket: VenueBook | null } | null {
  const { target, ticketKey, openTicket } = args;
  const { synth, teamSide } = target;
  const kalshi: VenueBook | null =
    (teamSide === 'away' ? synth.kalshi?.away : synth.kalshi?.home) ?? null;
  const polymarket: VenueBook | null =
    (teamSide === 'away' ? synth.polymarket?.away : synth.polymarket?.home) ?? null;
  if (!kalshi && !polymarket) return null;

  const listed = (v: PropVenue) => (v === 'kalshi' ? kalshi != null : polymarket != null);
  const sticky = openTicket != null && openTicket.key === ticketKey && listed(openTicket.venue);
  return {
    venue: sticky ? openTicket!.venue : kalshi ? 'kalshi' : 'polymarket',
    kalshi,
    polymarket,
  };
}

export function propRowSeedOnToggle(args: {
  /** The row currently expanded (compact: expandedBandKey; detailed: expanded key). */
  prevOpenKey: string | null;
  /** This row's expand key (compact: the ladder groupKey; detailed: the synth key). */
  rowKey: string;
  /** The key ticket ownership is stored by — the ladder groupKey. Defaults to
   *  `rowKey` (they coincide in Compact; Detailed rows expand by synth key). */
  ticketKey?: string;
  /** The selected rung's own synth + side — `lad.target` compact, the row's synth detailed. */
  target: LadderTarget;
  /** Which ladder/row + venue owns the open ticket (openBandTicket). */
  openTicket: PropTicketOwner | null;
  marketSide: 'yes' | 'no';
}): { ctx: TradeTicketContext; venue: PropVenue; chipKey: string } | null {
  const { prevOpenKey, rowKey, target, openTicket, marketSide } = args;
  const ticketKey = args.ticketKey ?? rowKey;
  if (prevOpenKey === rowKey) return null;

  const { synth, teamSide } = target;
  // Venue + books come from the shared rule — see resolveRungVenue.
  const resolved = resolveRungVenue({ target, ticketKey, openTicket });
  if (!resolved) return null;
  const { venue, kalshi, polymarket } = resolved;

  return {
    venue,
    // The rung's stable chip id (its market ticker) — what rungKeyByGroup
    // stores, so a Detailed-row seed can point the ladder's selection here.
    chipKey: chipKeyFor(kalshi, polymarket, `${synth.key}|${teamSide}`),
    ctx: {
      game: synth,
      teamSide,
      venue,
      // No prefillPrice: a row open carries no price — the panel seeds from
      // best-of-book.
      initialOrderSide: marketSide,
      postOnly: false,
    },
  };
}
